import { useState } from "react";
import { useFetch, apiPost, timeAgo } from "@/lib/shieldx";
import { PageHeader, Loader } from "@/components/shieldx/Primitives";
import { StatusBadge, RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import { Bot, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const FLOW = ["Identity", "Authentication", "Authorization", "Policy Check", "Risk Check", "Action"];

export default function AIAgents() {
  const { data, loading, reload } = useFetch("/ai-agents");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  if (loading || !data) return <Loader label="Loading AI agent registry" />;

  const revoke = async () => {
    setBusy(true);
    try { await apiPost(`/ai-agents/${sel.id}/revoke`, {}); toast.success("Agent credentials revoked — isolated"); setSel(null); reload(); }
    catch { toast.error("Revoke failed"); } finally { setBusy(false); }
  };

  const columns = [
    { key: "name", label: "Agent", render: (r) => (
      <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.model}</p></div>
    ) },
    { key: "data_access", label: "Data Access", render: (r) => <span className="text-xs text-slate-400">{r.data_access}</span> },
    { key: "permissions", label: "Permissions", render: (r) => <span className="font-mono text-[11px] text-slate-400">{(r.permissions || []).length} scoped</span> },
    { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "last_activity", label: "Last Active", render: (r) => <span className="text-xs text-slate-500">{timeAgo(r.last_activity)}</span> },
  ];

  return (
    <div>
      <PageHeader icon={Bot} title="AI Agent Registry"
        subtitle="Every AI agent is a controlled digital identity. No agent can automatically increase its own authority." actions={<DemoTag />} />
      <Table columns={columns} rows={data} testid="ai-agents-table" onRowClick={(r) => setSel(r)} />

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg border-slate-800 bg-slate-900 text-slate-100">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{sel.name}</DialogTitle>
                <DialogDescription className="font-mono text-xs text-slate-500">{sel.model} · {sel.provider} · {sel.environment}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Risk"><RiskBadge value={sel.risk_level} /></Info>
                <Info label="Status"><StatusBadge value={sel.status} /></Info>
              </div>
              <div className="space-y-3">
                <Block label="Tools" items={sel.tools} />
                <Block label="Permissions" items={sel.permissions} />
                <Info label="Data Access"><span className="text-slate-300">{sel.data_access}</span></Info>
                <Info label="Authorization Scope"><span className="text-slate-300">{sel.authorization_scope}</span></Info>
              </div>
              <div>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Permission Flow</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {FLOW.map((f, i) => (
                    <span key={f} className="flex items-center gap-1.5">
                      <span className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 font-mono text-[10px] text-slate-300">{f}</span>
                      {i < FLOW.length - 1 && <span className="text-slate-600">→</span>}
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">Self-escalation blocked {sel.privilege_escalation_blocked}× — capability never grants authority.</p>
              </div>
              <button onClick={revoke} disabled={busy} data-testid="agent-revoke"
                className="mt-2 flex items-center justify-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 py-2.5 text-sm font-semibold text-red-300 hover:bg-red-500/20 disabled:opacity-50">
                <ShieldOff className="h-4 w-4" /> Revoke credentials & isolate agent
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, children }) {
  return <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3"><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p><div className="mt-1.5">{children}</div></div>;
}
function Block({ label, items }) {
  return (
    <div>
      <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <div className="flex flex-wrap gap-1.5">{(items || []).map((i) => <span key={i} className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 font-mono text-xs text-slate-300">{i}</span>)}</div>
    </div>
  );
}
